import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import {
  Facebook,
  Instagram,
  LayoutDashboard,
  LogOut,
  Menu,
  ShieldCheck,
  User,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { homeContentQuery } from "@/lib/api";

const NAV = [
  { label: "Műhelyek", id: "workshops" },
  { label: "Események", id: "featured" },
  { label: "Órarend", id: "timetable" },
  { label: "Bérletek", id: "passes" },
  { label: "Rólam", id: "about" },
  { label: "Vendégkönyv", id: "guestbook", to: "/vendegkonyv" },
  { label: "Kapcsolat", id: "contact" },
];

const SOCIAL_DEFAULTS = {
  facebook_url: "https://www.facebook.com/sorrolsorra.art",
  instagram_url: "https://www.instagram.com/sorrolsorra.art",
};

const UserMenu = ({ user, onProfile, onAdmin, onLogout }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid="user-menu-trigger" aria-label="Fiókom menü" title="Fiókom"
        className="flex items-center justify-center h-10 w-10 rounded-full bg-poppy/10 text-poppy hover:bg-poppy hover:text-white transition-colors">
        <User size={18} strokeWidth={1.8} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56 bg-white" data-testid="user-menu-content">
      <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onProfile} data-testid="user-menu-profile" className="cursor-pointer">
        <LayoutDashboard size={16} strokeWidth={1.5} className="mr-2" /> Fiókom
      </DropdownMenuItem>
      {user.role === "admin" && (
        <DropdownMenuItem onClick={onAdmin} data-testid="user-menu-admin" className="cursor-pointer">
          <ShieldCheck size={16} strokeWidth={1.5} className="mr-2" /> Admin felület
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onLogout} data-testid="user-menu-logout" className="cursor-pointer text-red-500 focus:text-red-600">
        <LogOut size={16} strokeWidth={1.5} className="mr-2" /> Kilépés
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Navbar = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { data: content = {} } = useQuery(homeContentQuery);
  const socialLinks = {
    facebook_url: content.facebook_url || SOCIAL_DEFAULTS.facebook_url,
    instagram_url: content.instagram_url || SOCIAL_DEFAULTS.instagram_url,
  };
  const navigate = useNavigate();
  const location = useLocation();

  const go = (item) => {
    setOpen(false);
    if (item.to) {
      navigate(item.to);
      return;
    }
    const { id } = item;
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 350);
    } else {
      scroll();
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" className="shrink-0" data-testid="logo-home-link" onClick={() => window.scrollTo(0, 0)}>
          <Logo className="h-14 w-auto" />
        </Link>

        <nav className="hidden xl:flex items-center gap-8">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n)} data-testid={`nav-${n.id}`}
               className="text-sm text-ink/80 hover:text-poppy transition-colors duration-200">
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <div className="flex items-center gap-1 border-r border-black/10 pr-3">
            <a
              href={socialLinks.facebook_url}
              target="_blank"
              rel="noreferrer"
              aria-label="sorról sorra a Facebookon"
              title="Facebook"
              data-testid="header-facebook-link"
              className={
                "rounded-full p-2 text-ink/75 hover:bg-poppy/10 hover:text-poppy " +
                "transition-colors"
              }
            >
              <Facebook size={17} strokeWidth={1.8} />
            </a>
            <a
              href={socialLinks.instagram_url}
              target="_blank"
              rel="noreferrer"
              aria-label="sorról sorra az Instagramon"
              title="Instagram"
              data-testid="header-instagram-link"
              className={
                "rounded-full p-2 text-ink/75 hover:bg-poppy/10 hover:text-poppy " +
                "transition-colors"
              }
            >
              <Instagram size={17} strokeWidth={1.8} />
            </a>
          </div>
          {user ? (
            <UserMenu user={user}
              onProfile={() => navigate("/profil")}
              onAdmin={() => navigate("/admin")}
              onLogout={() => { logout(); navigate("/"); }} />
          ) : (
            <button onClick={() => navigate("/belepes")} data-testid="nav-login-btn"
              className="rounded-full bg-poppy text-white text-sm px-6 py-2.5 hover:bg-poppy/90 transition-colors duration-200">
              Belépés
            </button>
          )}
        </div>

        <div className="xl:hidden flex items-center gap-1.5">
          {user && (
            <UserMenu user={user}
              onProfile={() => { navigate("/profil"); setOpen(false); }}
              onAdmin={() => { navigate("/admin"); setOpen(false); }}
              onLogout={() => { logout(); navigate("/"); setOpen(false); }} />
          )}
          <button className="text-ink" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-black/5 px-5 py-4 space-y-3" data-testid="mobile-menu">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n)} data-testid={`mobile-nav-${n.id}`}
               className="block w-full text-left text-sm text-ink py-1.5">{n.label}</button>
          ))}
          <div className="flex items-center gap-2 pt-2" data-testid="mobile-social-links">
            <a
              href={socialLinks.facebook_url}
              target="_blank"
              rel="noreferrer"
              aria-label="sorról sorra a Facebookon"
              title="Facebook"
              data-testid="mobile-header-facebook-link"
              className={
                "rounded-full bg-poppy/10 p-2 text-poppy hover:bg-poppy hover:text-white " +
                "transition-colors"
              }
            >
              <Facebook size={17} strokeWidth={1.8} />
            </a>
            <a
              href={socialLinks.instagram_url}
              target="_blank"
              rel="noreferrer"
              aria-label="sorról sorra az Instagramon"
              title="Instagram"
              data-testid="mobile-header-instagram-link"
              className={
                "rounded-full bg-poppy/10 p-2 text-poppy hover:bg-poppy hover:text-white " +
                "transition-colors"
              }
            >
              <Instagram size={17} strokeWidth={1.8} />
            </a>
          </div>
          <div className="pt-3 border-t border-black/5 flex flex-col gap-2">
            {!user && (
              <button onClick={() => { navigate("/belepes"); setOpen(false); }} className="rounded-full bg-poppy text-white text-sm px-6 py-2.5 w-full">Belépés</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
